import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const useToolCard = (id: string, initialLikes: number = 0) => {
  const navigate = useNavigate();
  const user = useUser();
  const [likeCount, setLikeCount] = useState(initialLikes);
  const [hasLiked, setHasLiked] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkUserLike = async () => {
      if (!user) {
        setHasLiked(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tool_interactions')
          .select('id')
          .eq('tool_id', id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking user like:', error);
          return;
        }

        setHasLiked(!!data);
      } catch (error) {
        console.error('Error checking user like:', error);
      }
    };

    checkUserLike();
  }, [id, user]);

  useEffect(() => {
    const channel = supabase
      .channel(`tool-likes-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tool_interactions',
          filter: `tool_id=eq.${id}`
        },
        async () => {
          try {
            const { count } = await supabase
              .from('tool_interactions')
              .select('id', { count: 'exact' })
              .eq('tool_id', id);
            
            setLikeCount(count || 0);
            
            if (user) {
              const { data } = await supabase
                .from('tool_interactions')
                .select('id')
                .eq('tool_id', id)
                .eq('user_id', user.id)
                .maybeSingle();
              
              setHasLiked(!!data);
            }

            queryClient.invalidateQueries({ queryKey: ['tools'] });
          } catch (error) {
            console.error('Error updating likes:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, user, queryClient]);

  const handleLike = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    try {
      if (hasLiked) {
        const { error } = await supabase
          .from('tool_interactions')
          .delete()
          .eq('tool_id', id)
          .eq('user_id', user.id);

        if (error) throw error;
        
        setHasLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        const { error } = await supabase
          .from('tool_interactions')
          .insert([
            { tool_id: id, user_id: user.id, likes: 1 }
          ]);

        if (error) throw error;
        
        setHasLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error("حدث خطأ أثناء تسجيل الإعجاب");
      // Revert optimistic update
      setHasLiked(prev => !prev);
      setLikeCount(prev => hasLiked ? prev + 1 : prev - 1);
    }
  };

  const handleCardClick = () => {
    navigate(`/tool/${id}`);
  };

  return {
    likeCount,
    hasLiked,
    handleLike,
    handleCardClick,
  };
};